import React, { useState, useCallback, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { Button } from "devextreme-react/button";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import { LoadPanel } from "devextreme-react/load-panel";
import Toolbar, { Item } from "devextreme-react/toolbar";
import axios from "axios";
import notify from "devextreme/ui/notify";
import { confirm } from "devextreme/ui/dialog";
import data from "../../assets/apiRoutes.json";
import { GetFormatedDateWithTime } from "../../common/Utils";
import { CONCURRENCY_HANDLED, getApiErrorMessage } from "../../common/concurrency";
import { MODULE_IM, RECORD_TYPE_R, STATUS_OPEN, TRANSACTION_TYPE_USAGE, STAGE_COMPLETED } from "../itemManagement/transactionsTab/codeLists";
import PrimaryTab from "./PrimaryTab";
import AdditionalTab from "./AdditionalTab";
import UserDefinedTab from "./UserDefinedTab";
import DetailsTab from "./DetailsTab";
import { EMPTY_GUID } from "../../const/constants";

const RANGE10 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const LIST_URL = "/im/EnterTransaction";

// Header module/record-type are server-authoritative; the form defaults to an Open transaction.
// Delphi qryIMTransactionHeaderAfterInsert (uIMM0019.pas:975) seeds a new header for this screen
// with RECORDTYPE 'R', TRANSACTIONTYPE 'U', TRANSACTIONDATE Now, STATUS 'O' and STAGE 'C'. The
// date is seeded here rather than left to the server so the user sees it before saving, which is
// what Delphi does - the field is populated the moment the record is inserted.
const buildInitialFields = () => {
    const init = {
        ROWID: EMPTY_GUID,
        RECORD: "",
        MODULE: MODULE_IM,
        RECORDTYPE: RECORD_TYPE_R,
        RPROFILE: "",
        PROFILECODE: "",
        TRANSACTIONTYPE: TRANSACTION_TYPE_USAGE,
        REFERENCE: "",
        TRANSACTIONDATE: new Date(),
        STAGE: STAGE_COMPLETED,
        STATUS: STATUS_OPEN,
        DESCRIPTION: "",
        FISCALYEAR: "",
        FISCALPERIOD: "",
        COMMENT: "",
        IMPORTFILENAME: "",
        USERDEFINEDCOMMENT: "",
        RDEFAULTDEPARTMENT: "",
        RDEFAULTFUND: "",
        RDEFAULTJOB: "",
        // Read-only rollups from the detail lines.
        TOTALLINES: "",
        TOTALQUANTITY: "",
        TOTALAMOUNT: "",
        // Opaque optimistic-concurrency token (rule 16). Loaded, echoed on save, replaced by the save result.
        CONCURRENCYTOKEN: null,
    };
    RANGE10.forEach((i) => {
        init[`CODE${i}`] = "";
        init[`REFERENCE${i}`] = "";
        init[`DATE${i}`] = "";
        init[`QUANTITY${i}`] = "";
    });
    return init;
};

// DTO (PascalCase JSON) -> formFields (UPPERCASE) on load/edit.
const mapDtoToFields = (item) => {
    const f = {
        ROWID: item.Rowid || EMPTY_GUID,
        RECORD: item.RecordNumber || "",
        MODULE: item.Module || MODULE_IM,
        RECORDTYPE: item.RecordType || RECORD_TYPE_R,
        RPROFILE: item.RProfile || "",
        PROFILECODE: item.ProfileCode || "",
        TRANSACTIONTYPE: item.TransactionType || "",
        REFERENCE: item.Reference || "",
        TRANSACTIONDATE: item.TransactionDate || "",
        STAGE: item.Stage || "",
        STATUS: item.Status || STATUS_OPEN,
        DESCRIPTION: item.Description || "",
        FISCALYEAR: item.FiscalYear ?? "",
        FISCALPERIOD: item.FiscalPeriod ?? "",
        COMMENT: item.Comment || "",
        IMPORTFILENAME: item.ImportFileName || "",
        USERDEFINEDCOMMENT: item.UserDefinedComment || "",
        RDEFAULTDEPARTMENT: item.RDefaultDepartment || "",
        RDEFAULTFUND: item.RDefaultFund || "",
        RDEFAULTJOB: item.RDefaultJob || "",
        TOTALLINES: item.TotalLines ?? "",
        TOTALQUANTITY: item.TotalQuantity != null ? Number(item.TotalQuantity).toFixed(2) : "",
        TOTALAMOUNT: item.TotalAmount != null ? Number(item.TotalAmount).toFixed(2) : "",
        CONCURRENCYTOKEN: item.ConcurrencyToken || null,
    };
    RANGE10.forEach((i) => {
        f[`CODE${i}`] = item[`Code${i}`] || "";
        f[`REFERENCE${i}`] = item[`Reference${i}`] || "";
        f[`DATE${i}`] = item[`Date${i}`] || "";
        f[`QUANTITY${i}`] = item[`Quantity${i}`] ?? "";
    });
    return f;
};

// formFields (UPPERCASE) -> DTO payload (PascalCase) on save.
const mapFieldsToPayload = (f, isUpdate) => {
    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const payload = {
        Rowid: isUpdate ? f.ROWID : EMPTY_GUID,
        Module: MODULE_IM,
        RecordType: RECORD_TYPE_R,
        RProfile: f.RPROFILE || null,
        TransactionType: f.TRANSACTIONTYPE || null,
        TransactionDate: f.TRANSACTIONDATE ? GetFormatedDateWithTime(f.TRANSACTIONDATE) : null,
        Reference: f.REFERENCE || null,
        Description: f.DESCRIPTION || null,
        Stage: f.STAGE || null,
        Status: f.STATUS || null,
        Comment: f.COMMENT || null,
        FiscalYear: num(f.FISCALYEAR),
        FiscalPeriod: num(f.FISCALPERIOD),
        RDefaultDepartment: f.RDEFAULTDEPARTMENT || null,
        RDefaultFund: f.RDEFAULTFUND || null,
        RDefaultJob: f.RDEFAULTJOB || null,
        UserDefinedComment: f.USERDEFINEDCOMMENT || null,
        // Tells the API which row version this edit started from. Null on insert.
        ConcurrencyToken: f.CONCURRENCYTOKEN || null,
        // Opt into BeforePost warnings (e.g. the STATUS auto-reset), which are non-blocking and informational.
        IgnoreWarnings: false,
    };
    RANGE10.forEach((i) => {
        payload[`Code${i}`] = f[`CODE${i}`] || null;
        payload[`Reference${i}`] = f[`REFERENCE${i}`] || null;
        payload[`Date${i}`] = f[`DATE${i}`] ? GetFormatedDateWithTime(f[`DATE${i}`]) : null;
        payload[`Quantity${i}`] = num(f[`QUANTITY${i}`]);
    });
    return payload;
};

function Form(props) {
    const history = useHistory();
    const formRefs = useRef({});
    const accessToken = props?.oidc?.user?.access_token;

    const [formFields, setformFields] = useState(buildInitialFields());
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);

    const hideLoadPanel = useCallback(() => setLoadPanelVisible(false), []);
    const position = { of: "#accordion" };

    const handleFieldChange = useCallback((name, value) => {
        setformFields((prev) => ({ ...prev, [name]: value }));
    }, []);

    const loadById = useCallback(async (rowId) => {
        if (!rowId || rowId === EMPTY_GUID) return;
        try {
            setLoadPanelVisible(true);
            const url = `${window._env_.REACT_APP_APIS}/${data.IM.IMTransactionHeader.GetTransaction}?rowId=${rowId}`;
            const response = await axios.get(url, {
                headers: { access_token: accessToken, Authorization: `Bearer ${accessToken}` },
            });
            if (response.data && response.data.Rowid) {
                setformFields(mapDtoToFields(response.data));
            }
        } catch (error) {
            notify(getApiErrorMessage(error, "Failed to load transaction."), "error", 5000);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [accessToken]);

    useEffect(() => {
        const rowId = props.id;
        if (rowId && rowId !== EMPTY_GUID) loadById(rowId);
    }, [props.id, loadById]);

    // Re-read the header after a detail line is added/updated/deleted so Total Lines/Quantity/Amount refresh
    // (they come from the server projection). Only the read-only rollups are updated — unsaved Primary-tab
    // edits are preserved. Mirrors Delphi GetTransTotals on qeTransactionDetailAfterCommit (uIMD0019.PAS:1727).
    const refreshHeaderTotals = useCallback(async () => {
        const rowId = formFields.ROWID;
        if (!rowId || rowId === EMPTY_GUID) return;
        try {
            const url = `${window._env_.REACT_APP_APIS}/${data.IM.IMTransactionHeader.GetTransaction}?rowId=${rowId}`;
            const response = await axios.get(url, {
                headers: { access_token: accessToken, Authorization: `Bearer ${accessToken}` },
            });
            const item = response.data;
            if (item && item.Rowid) {
                setformFields((prev) => ({
                    ...prev,
                    TOTALLINES: item.TotalLines ?? prev.TOTALLINES,
                    TOTALQUANTITY: item.TotalQuantity != null ? Number(item.TotalQuantity).toFixed(2) : prev.TOTALQUANTITY,
                    TOTALAMOUNT: item.TotalAmount != null ? Number(item.TotalAmount).toFixed(2) : prev.TOTALAMOUNT,
                }));
            }
        } catch (error) {
            // Non-fatal: the line change already succeeded; leave the stale totals rather than interrupt the user.
        }
    }, [formFields.ROWID, accessToken]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoadPanelVisible(true);
        try {
            const isUpdate = formFields.ROWID && formFields.ROWID !== EMPTY_GUID;
            const payload = mapFieldsToPayload(formFields, isUpdate);
            const endpoint = isUpdate ? data.IM.IMTransactionHeader.UpdateTransaction : data.IM.IMTransactionHeader.InsertTransaction;
            const method = isUpdate ? "put" : "post";
            const url = `${window._env_.REACT_APP_APIS}/${endpoint}`;

            const resp = await axios[method](url, payload, {
                headers: { access_token: accessToken, Authorization: `Bearer ${accessToken}` },
            });

            const saved = resp?.data?.Data;
            if (saved) {
                setformFields((prev) => ({
                    ...prev,
                    ROWID: saved.Rowid || prev.ROWID,
                    RECORD: saved.RecordNumber || prev.RECORD,
                    PROFILECODE: saved.ProfileCode || prev.PROFILECODE,
                    // The server may auto-reset STATUS (BeforePost rule) — adopt what was actually saved.
                    STATUS: saved.Status || prev.STATUS,
                    TOTALLINES: saved.TotalLines ?? prev.TOTALLINES,
                    TOTALQUANTITY: saved.TotalQuantity != null ? Number(saved.TotalQuantity).toFixed(2) : prev.TOTALQUANTITY,
                    TOTALAMOUNT: saved.TotalAmount != null ? Number(saved.TotalAmount).toFixed(2) : prev.TOTALAMOUNT,
                    // Adopt the token for the version just written so the next save doesn't falsely conflict.
                    CONCURRENCYTOKEN: saved.ConcurrencyToken || null,
                }));
            }
            notify(resp?.data?.Message || "Transaction saved successfully.", "success", 3000);
            // Non-blocking BeforePost warnings (e.g. the STATUS auto-reset) — inform the user; the save succeeded.
            const warnings = resp?.data?.Warnings;
            if (Array.isArray(warnings)) {
                warnings.forEach((w) => notify(w, "warning", 6000));
            }
        } catch (err) {
            // A stale save is handled globally by the axios interceptor (the reload dialog). Don't stack a toast.
            if (err?.[CONCURRENCY_HANDLED]) return;
            notify(getApiErrorMessage(err, "Failed to save transaction."), "error", 5000);
        } finally {
            hideLoadPanel();
        }
    };

    // Delphi uIMD0019 "Delete Header Record and Associated Detail Records". Existing records only; a
    // successful delete shows a green toast and returns to the listing (project delete-toast convention).
    const handleDelete = async () => {
        if (!formFields.ROWID || formFields.ROWID === EMPTY_GUID) return;
        const confirmed = await confirm("Delete selected record?", "Confirm");
        if (!confirmed) return;
        setLoadPanelVisible(true);
        try {
            const url = `${window._env_.REACT_APP_APIS}/${data.IM.IMTransactionHeader.DeleteTransaction}?rowId=${formFields.ROWID}`;
            const resp = await axios.delete(url, {
                headers: { access_token: accessToken, Authorization: `Bearer ${accessToken}` },
            });
            notify(resp?.data?.Message || "Deleted successfully.", "success", 3000);
            history.push(LIST_URL);
        } catch (err) {
            if (err?.[CONCURRENCY_HANDLED]) return;
            notify(getApiErrorMessage(err, "Failed to delete transaction."), "error", 5000);
        } finally {
            hideLoadPanel();
        }
    };

    const addNewRecordClick = () => setformFields(buildInitialFields());

    const handleReset = () => {
        const rowId = props.id;
        if (rowId && rowId !== EMPTY_GUID) loadById(rowId);
        else setformFields(buildInitialFields());
    };

    const goBack = () => history.push(LIST_URL);

    return (
        <div>
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion">
                        <LoadPanel
                            shadingColor="rgba(0,0,0,0.4)"
                            position={position}
                            onHiding={hideLoadPanel}
                            visible={loadPanelVisible}
                            showIndicator={true}
                            shading={true}
                            showPane={true}
                            hideOnOutsideClick={false}
                        />
                        <form onSubmit={(e) => handleFormSubmit(e)}>
                            <Toolbar className="header">
                                <Item location="before">
                                    <div className="title">
                                        <h6 style={{ color: window._env_.Color2, marginLeft: "12px" }}>
                                        Enter Transaction
                                        </h6>
                                    </div>
                                </Item>
                                <Item location="after">
                                    <Button text="Save" useSubmitBehavior={true} type="default" className="accordianButton" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={addNewRecordClick} text="New" type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                                </Item>
                                {formFields.ROWID && formFields.ROWID !== EMPTY_GUID && (
                                    <Item location="after">
                                        <Button className="accordianButton" onClick={handleDelete} text="Delete" type="default" />
                                    </Item>
                                )}
                                <Item location="after">
                                    <Button className="accordianButton" onClick={goBack} text="Back" />
                                </Item>
                            </Toolbar>
                            <TabPanel deferRendering={props.id ? true : false} showNavButtons={true} height={"auto"}>
                                <TabItem title="Primary">
                                    <PrimaryTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                                <TabItem title="Additional">
                                    <AdditionalTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                                <TabItem title="User Defined">
                                    <UserDefinedTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                                <TabItem title="Details">
                                    <DetailsTab headerId={formFields.ROWID} transactionType={formFields.TRANSACTIONTYPE} status={formFields.STATUS} onDetailsChanged={refreshHeaderTotals} />
                                </TabItem>
                            </TabPanel>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(Form);
